import React from 'react';
import { createPortal } from 'react-dom';
import { Menu, X } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import TransparentLogo from '../assets/transparent logo.png';


const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Events', path: '/events' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <nav className="bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100/90 fixed top-0 left-0 w-full z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 lg:h-[4.25rem]">

          {/* Logo */}
          <Link to="/" className="flex items-center pl-3 lg:pl-6">
            <img
              src={TransparentLogo}
              alt="Domigo"
              className="h-12 w-auto object-contain transition-transform duration-300 hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1.5">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative px-3 py-1 text-[16px] font-semibold tracking-[0.01em] transition-all duration-200 rounded-xl ${isActive(link.path)
                    ? 'text-domigo-pink bg-domigo-pink/10 shadow-sm'
                    : 'text-gray-700 hover:text-domigo-pink hover:bg-gray-50/90'
                  }`}
              >
                {link.name}
                {isActive(link.path) && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-domigo-pink rounded-full"></span>
                )}
              </Link>
            ))}

            <div className="pl-3 ml-2 border-l border-gray-200 flex items-center space-x-3">
              <Link
                to="/contact"
                className="relative overflow-hidden bg-gradient-pink text-white px-5 py-2 rounded-full text-[14px] font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 hover:-translate-y-0.5 btn-smooth tracking-[0.01em]"
              >
                <span className="relative z-10">Get In Touch</span>
              </Link>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              type="button"
              onClick={() => {
                setIsMenuOpen((s) => !s);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIsMenuOpen((s) => !s);
                }
              }}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
              className="p-1.5 rounded-full text-gray-700 bg-transparent hover:bg-transparent transition-colors duration-150 relative z-[10000] border-0 shadow-none focus:outline-none"
              style={{ background: 'transparent', boxShadow: 'none', border: 'none' }}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X size={24} strokeWidth={2} /> : <Menu size={24} strokeWidth={2} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation - Full Screen Overlay */}
      {isMenuOpen && createPortal(
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm lg:hidden z-[9990] animate-fade-in pointer-events-auto"
            onClick={() => setIsMenuOpen(false)}
          />

          {/* Mobile Menu rendered at document.body via portal to avoid stacking-context issues */}
          <div id="mobile-menu" className="fixed top-16 left-0 right-0 bottom-0 lg:hidden z-[9995] animate-slide-up pointer-events-auto" aria-hidden={!isMenuOpen}>
            <div className="bg-white h-full overflow-y-auto shadow-2xl">
              <div className="px-4 py-6 space-y-1">
                {navLinks.map((link) => (
                  <a
                    key={link.path}
                    href={link.path}
                    onClick={(e) => {
                      e.preventDefault();
                      setIsMenuOpen(false);
                      navigate(link.path);
                    }}
                    className={`flex items-center justify-between px-4 py-3.5 text-lg font-semibold rounded-xl transition-all duration-200 ${isActive(link.path)
                        ? 'text-domigo-pink bg-domigo-pink/10 shadow-sm'
                        : 'text-gray-700 hover:text-domigo-pink hover:bg-gray-50'
                      }`}
                  >
                    <span>{link.name}</span>
                    {isActive(link.path) && (
                      <div className="w-2 h-2 bg-domigo-pink rounded-full"></div>
                    )}
                  </a>
                ))}

                <div className="pt-4 mt-4 border-t border-gray-100 space-y-1">
                  <a
                    href="/contact"
                    onClick={(e) => { e.preventDefault(); setIsMenuOpen(false); navigate('/contact'); }}
                    className="flex items-center justify-center bg-gradient-pink text-white px-6 py-4 rounded-xl text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300 mt-4 btn-smooth"
                  >
                    Get In Touch
                  </a>
                </div>

                {/* Mobile Menu Footer */}
                <div className="pt-6 mt-6 border-t border-gray-100">
                  <p className="text-center text-sm text-gray-500">
                    Premium Pet Care Services
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>, document.body
      )}
    </nav>
  );
};

export default Navbar;
